import { Component, OnDestroy, OnInit } from '@angular/core';
import { ModalController } from '@ionic/angular';
import * as JSZip from 'jszip';
import { Subscription } from 'rxjs';
import { QrCodeService } from 'src/app/core/services/behavior/qr-code.service';
import { AuthenticationService } from 'src/app/core/services/http/authentication.service';
import { MesaService } from 'src/app/core/services/http/mesa.service';
import { ModalRoleEnum } from '../../enums/modal-role.enum';
import { TipoAtendimentoEnum } from '../../enums/tipo-atendimento.enum';
import { Mesa } from '../../models/mesa';
import { saveAs } from 'file-saver';
import { FormBuilder, FormControl, Validators } from '@angular/forms';
import { MesaRequest } from '../../models/mesa-request';

@Component({
  selector: 'app-manutencao-mesas-dialog',
  templateUrl: './manutencao-mesas-dialog.component.html',
  styleUrls: ['./manutencao-mesas-dialog.component.scss']
})
export class ManutencaoMesasDialogComponent implements OnInit, OnDestroy {
  public readonly categorias = [
    'MESA',
    'DELIVERY',
    'BALCAO'
  ];

  private _jsZip = new JSZip();
  private _subs$: Subscription[] = [];

  public form = this._formBuilder.group({
    numeroInicialCtrl: new FormControl<number>(1, [Validators.required]),
    numeroFinalCtrl: new FormControl<number>(null),
    grupoCtrl: new FormControl<TipoAtendimentoEnum>(TipoAtendimentoEnum.MESA),
  });
  public mesasCadastradas: Mesa[] = [];
  public tipoAtendimentoMesa = TipoAtendimentoEnum.MESA;

  constructor(
    private _formBuilder: FormBuilder,

    private _modalCtrl: ModalController,
    private _authService: AuthenticationService,
    private _mesaService: MesaService,
    private _qrcodeService: QrCodeService
  ) { }

  public async cancelarAsyncOnClick() {
    await this._modalCtrl.dismiss(null, ModalRoleEnum.CANCEL);
  }

  public excluirMesaOnClick(mesa: Mesa) {
    const sub$ = this._mesaService
      .excluirMesa(mesa.numeroMesa)
      .subscribe(res => {
        if (res.success) {
          const index = this.mesasCadastradas.indexOf(mesa);

          if (index >= 0) this.mesasCadastradas.splice(index, 1);
        }
      });

    this._subs$.push(sub$);
  }

  public async gerarQrCodeAsyncOnClick(mesa: Mesa) {
    const baseUrlQrCode = this._authService.baseUrlQrCode;
    const request = `${baseUrlQrCode}/#/${mesa.guid}`;

    const qrCode = await this._gerarQrCodeAsync(request);
    const fileName = `mesa-${mesa.numeroMesa}.png`;

    const blob = this._jsonStringParaBlob(qrCode);

    saveAs(blob, fileName);
  }

  public incluirMesasOnClick() {
    const requests = this._obterMesasRequest();

    const sub$ = this._mesaService
      .criarVariasMesas(requests)
      .subscribe(res => {
        if (res.success) {
          this._resetarForm();
          this._carregarMesasCadastradas();
        }
      });

    this._subs$.push(sub$);
  }

  public ngOnDestroy(): void {
    this._subs$.forEach(l => l.unsubscribe());
  }

  public ngOnInit() {
    this._carregarMesasCadastradas();
  }

  private _carregarMesasCadastradas() {
    this.mesasCadastradas = [];

    const sub$ = this._mesaService
      .obterMesasCadastradas()
      .subscribe(res => this.mesasCadastradas = res.success ? res.data : []);

    this._subs$.push(sub$);
  }

  private _criarSequenciaNumerica(valorInicial: number, valorFinal: number): number[] {
    const arr = [];

    for (let i = valorInicial; i <= valorFinal; i++) {
      arr.push(i);
    }

    return arr;
  }

  private async _gerarQrCodeAsync(data: string): Promise<string> {
    return await this._qrcodeService.gerarQrCodeAsync(data);
  }

  private _jsonStringParaBlob(json: string): Blob {
    const arr = json.split(',');
    const mimeMatch = arr[0].match(/:(.*?);/);
    const mime = mimeMatch ? mimeMatch[1] : 'application/octet-stream';

    const uintArray = new Uint8Array(Uint8Array.from(atob(arr[1]), char => char.charCodeAt(0)));

    return new Blob([uintArray], { type: mime });
  }

  private _obterMesasRequest(): MesaRequest[] {
    const form = this.form;

    const numeroInicial = form.get('numeroInicialCtrl').value;
    const numeroFinal = form.get('numeroFinalCtrl').value;
    const grupo = form.get('grupoCtrl').value;

    const requests: MesaRequest[] = [];

    let arrNumeroComandas: number[] = [];

    if (numeroInicial && numeroFinal) {
      arrNumeroComandas = this._criarSequenciaNumerica(numeroInicial, numeroFinal);

      arrNumeroComandas.forEach(numeroComanda => requests.push(new MesaRequest(numeroComanda, grupo)));
    } else if (numeroInicial && !numeroFinal) {
      requests.push(new MesaRequest(numeroInicial, grupo));
    }

    return requests;
  }

  private _resetarForm() {
    this.form.reset(
      {
        grupoCtrl: TipoAtendimentoEnum.MESA,
        numeroInicialCtrl: 1,
        numeroFinalCtrl: null
      }
    );
  }

  public async gerarQrCodeTodasMesasAsyncOnClick() {
    const arrMesas = this.mesasCadastradas.filter(l => l.grupo === TipoAtendimentoEnum.MESA);
    const baseUrlQrCode = this._authService.baseUrlQrCode;

    for (const mesa of arrMesas) {
      const request = `${baseUrlQrCode}/#/${mesa.guid}`;

      const qrCode = await this._gerarQrCodeAsync(request);

      const blob = this._jsonStringParaBlob(qrCode);

      this._jsZip.file(`mesa-${mesa.numeroMesa}.png`, blob);
    };

    const zipBlob = await this._jsZip.generateAsync({ type: 'blob' });

    saveAs(zipBlob, 'qr-codes.zip');
  }
}
